import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  parseApiError,
  hasErrors as checkHasErrors,
} from "../../utils/errorUtils";
import { useToast } from "../components";

/**
 * Custom hook for managing form validation errors
 *
 * Provides a clean interface for setting, clearing, and checking errors
 * Integrates with the parseApiError utility for handling API responses
 *
 * @example
 * const { errors, setFieldError, setApiErrors, clearErrors, hasErrors } = useErrorHandling();
 *
 * // Set individual field error
 * setFieldError('email', 'Email is required');
 *
 * // Handle API error response
 * try {
 *   await api.save();
 * } catch (error) {
 *   setApiErrors(error);
 * }
 *
 * // Clear all errors
 * clearErrors();
 */
export function useErrorHandling() {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const navigate = useNavigate();
  const { showError } = useToast();

  /**
   * Set a single field error
   */
  const setFieldError = useCallback((field: string, message: string) => {
    setErrors((prev) => ({
      ...prev,
      [field]: message,
    }));
  }, []);

  /**
   * Set multiple field errors at once
   */
  const setFieldErrors = useCallback((newErrors: Record<string, string>) => {
    setErrors((prev) => ({
      ...prev,
      ...newErrors,
    }));
  }, []);

  /**
   * Parse and set errors from API response
   * Automatically extracts field errors, line item errors, and global errors
   * Handles auth errors by redirecting to login instead of displaying
   */
  const setApiErrors = useCallback(
    (error: any) => {
      const parsed = parseApiError(error);

      // Handle authentication errors (401/403) - redirect instead of showing in form
      if (parsed.requiresAuth || parsed.statusCode === 401) {
        // Show toast notification
        showError(parsed.message);

        // Clear any existing form data from localStorage if needed
        // localStorage.removeItem('user');
        // localStorage.removeItem('token');

        // Redirect to login page
        setTimeout(() => {
          navigate("/");
        }, 1500); // Small delay so user can see the toast

        return; // Don't set form errors
      }

      // Handle permission errors (403) - show error but stay on page
      if (parsed.statusCode === 403) {
        showError(parsed.message);
        // Don't set form errors, just show toast
        return;
      }

      // Combine all errors into one object
      const allErrors: Record<string, string> = {
        ...parsed.fieldErrors,
      };

      // Add line item errors with proper keys
      Object.entries(parsed.lineItemErrors).forEach(([rowIndex, rowErrors]) => {
        rowErrors.forEach((errorMsg, idx) => {
          allErrors[`lineItem_${rowIndex}_error${idx}`] = errorMsg;
        });
      });

      // Add global errors
      parsed.globalErrors.forEach((errorMsg, idx) => {
        allErrors[`global_${idx}`] = errorMsg;
      });

      // If we have a main message but no specific errors, add it as a global error
      if (Object.keys(allErrors).length === 0 && parsed.message) {
        allErrors["_global"] = parsed.message;
      }

      setErrors(allErrors);
    },
    [navigate, showError]
  );

  /**
   * Clear a specific field error
   */
  const clearFieldError = useCallback((field: string) => {
    setErrors((prev) => {
      if (!(field in prev)) return prev;
      const { [field]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  /**
   * Clear all errors
   */
  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  /**
   * Check if there are any errors
   */
  const hasErrors = useCallback(() => {
    return checkHasErrors(errors);
  }, [errors]);

  /**
   * Get error for a specific field
   */
  const getFieldError = useCallback(
    (field: string): string | undefined => {
      return errors[field];
    },
    [errors]
  );

  /**
   * Check if a specific field has an error
   */
  const hasFieldError = useCallback(
    (field: string): boolean => {
      return !!errors[field];
    },
    [errors]
  );

  /**
   * Get all line item errors
   * Returns object with row index as key and array of error messages
   */
  const getLineItemErrors = useCallback((): Record<number, string[]> => {
    const lineItemErrors: Record<number, string[]> = {};

    Object.entries(errors).forEach(([key, value]) => {
      const lineItemMatch = key.match(/^lineItem_(\d+)_(.+)$/);
      if (lineItemMatch) {
        const rowIndex = parseInt(lineItemMatch[1]);
        if (!lineItemErrors[rowIndex]) {
          lineItemErrors[rowIndex] = [];
        }
        lineItemErrors[rowIndex].push(value);
      }
    });

    return lineItemErrors;
  }, [errors]);

  /**
   * Check if a specific line item row has errors
   */
  const hasLineItemError = useCallback(
    (rowIndex: number): boolean => {
      return Object.keys(errors).some((key) =>
        key.startsWith(`lineItem_${rowIndex}_`)
      );
    },
    [errors]
  );

  return {
    errors,
    setFieldError,
    setFieldErrors,
    setApiErrors,
    clearFieldError,
    clearErrors,
    hasErrors,
    getFieldError,
    hasFieldError,
    getLineItemErrors,
    hasLineItemError,
  };
}

export default useErrorHandling;
